import React, { useState, useCallback } from 'react';
import DatasetPicker from '../components/dashboard-builder/DatasetPicker';
import SavedDashboardsList from '../components/dashboard-builder/SavedDashboardsList';
import DashboardCanvas from '../components/dashboard-builder/DashboardCanvas';
import BuilderChat from '../components/dashboard-builder/BuilderChat';
import { dashboardBuilderService } from '../services/dashboardBuilderService';
import type { DashboardWidget, DashboardFilter, BuilderChatMessage, SavedDashboard, SuggestionGroup, WidgetType } from '../types/dashboard.types';
import type { ChartPanelChartType } from '../components/chat/ChartPanel';
import type { Layout } from 'react-grid-layout';

type ViewState = 'picker' | 'saved' | 'builder';

/** Grouped suggestion chips for a new empty dashboard */
function getGroupedSuggestions(datasetNames: string[]): SuggestionGroup[] {
  const ds = datasetNames.map((n) => n.toLowerCase());
  const groups: SuggestionGroup[] = [];

  // Auto-build suggestions
  const autoSuggestions: string[] = [];
  if (ds.some((d) => d.includes('finance'))) {
    autoSuggestions.push('Build me a financial overview dashboard');
  }
  if (ds.some((d) => d.includes('abs') || d.includes('census'))) {
    autoSuggestions.push('Build me a demographics overview dashboard');
  }
  autoSuggestions.push('Build me a complete dashboard with KPIs, charts and a table');

  groups.push({
    category: 'Auto-Build Dashboard',
    icon: 'BIDashboard',
    suggestions: autoSuggestions,
  });

  // Always show KPIs
  groups.push({
    category: 'Key Performance Indicators',
    icon: 'SpeedHigh',
    suggestions: [
      'Add a KPI showing the total number of records',
      'Show the total dollar amount as a KPI card',
    ],
  });

  // Financial (if finance dataset)
  if (ds.some((d) => d.includes('finance'))) {
    groups.push({
      category: 'Financial Analysis',
      icon: 'Money',
      suggestions: [
        'Add a bar chart of budget vs actuals by cost centre',
        'Show a donut chart of spend by top 10 suppliers',
        'Add a line chart of monthly expenditure trend',
      ],
    });
  }

  // Census / Demographics (if ABS dataset)
  if (ds.some((d) => d.includes('abs') || d.includes('census'))) {
    groups.push({
      category: 'Census & Demographics',
      icon: 'People',
      suggestions: [
        'Add a bar chart of total population by suburb for 2021',
        'Show median household weekly income by SA2 area as a bar chart',
        'Add a KPI showing the total population across all suburbs',
      ],
    });
  }

  // Always show chart-related
  groups.push({
    category: 'Charts & Comparisons',
    icon: 'BarChart4',
    suggestions: [
      'Add a bar chart comparing the top 10 values',
      'Show a trend over time as a line chart',
      'Break down the totals as a donut chart',
    ],
  });

  return groups;
}

/** Flatten grouped suggestions into a simple string[] for the chat panel */
function flattenSuggestions(groups: SuggestionGroup[]): string[] {
  return groups.flatMap((g) => g.suggestions).slice(0, 6);
}

/** Compute grid position for a new widget by packing into available space */
function computeGridPosition(
  existingWidgets: DashboardWidget[],
  widgetW: number,
  widgetH: number,
): { gridX: number; gridY: number } {
  const COLS = 12;
  const colBottom = new Array(COLS).fill(0);

  for (const w of existingWidgets) {
    const x = w.gridX ?? 0;
    const y = w.gridY ?? 0;
    const gw = w.gridW ?? sizeToGridW(w.size, w.type);
    const gh = w.gridH ?? (w.type === 'kpi' || w.type === 'number' ? 2 : w.type === 'table' ? 5 : 4);
    for (let c = x; c < Math.min(x + gw, COLS); c++) {
      colBottom[c] = Math.max(colBottom[c], y + gh);
    }
  }

  let bestX = 0;
  let bestY = Infinity;
  for (let x = 0; x <= COLS - widgetW; x++) {
    let rowY = 0;
    for (let c = x; c < x + widgetW; c++) {
      rowY = Math.max(rowY, colBottom[c]);
    }
    if (rowY < bestY) {
      bestY = rowY;
      bestX = x;
    }
  }

  return { gridX: bestX, gridY: bestY };
}

// Curated per-dataset templates: guaranteed-correct, dashboard-parity starting
// points (tabs + filter bar + canonical SQL), offered when the matching
// dataset is selected.
const CURATED_TEMPLATES: Record<string, {
  key: string;
  label: string;
  dashName: string;
  loadedMessage: (n: number) => string;
  suggestions: string[];
}> = {
  finance: {
    key: 'finance',
    label: 'Finance Overview',
    dashName: 'Finance & Contract Expenditure',
    loadedMessage: (n) => `Finance Overview template loaded with ${n} widgets across 4 tabs. Use the tabs above the dashboard to navigate between Budget & Expenditure, Cost Centres, Projects, and Purchase Orders.`,
    suggestions: ['Add a filter for year', 'Show budget vs actuals by department', 'Add a KPI for total POs billed'],
  },
  people: {
    key: 'people',
    label: 'People Insights',
    dashName: 'People & Workforce Overview',
    loadedMessage: (n) => `People Insights template loaded with ${n} widgets across 4 tabs (Demographics, Departures, Leave Balances, Excessive Leave), using the governed dashboard's measures. The Directorate, Department, Employment Type and Gender filters apply across every widget.`,
    suggestions: ['Show new starters by month', 'Break down departures by directorate', 'Add leave taken by month'],
  },
};

function sizeToGridW(size: string, type?: string): number {
  if (size === 'small') {
    // KPI/number value cards sit 4-across (3 cols each) like the governed
    // dashboards' KPI row; other small widgets stay 3-across.
    return type === 'kpi' || type === 'number' ? 3 : 4;
  }
  if (size === 'large') return 12;
  return 6;
}

/**
 * Dataset-aware default palettes — mirror the governed dashboards' accents so
 * built widgets look native to their domain (People Insights cyan, Finance
 * brand green, Property blue, …). Used when the AI doesn't pick colours.
 */
const DATASET_PALETTES: Record<string, string[]> = {
  // People Insights' categorical palette (DIR_PALETTE) — cyan-led but
  // multi-hue so adjacent categories stay distinguishable.
  'people-workforce':   ['#06B6D4', '#0891B2', '#7C3AED', '#2e873d', '#D97706', '#DC2626', '#0369A1', '#6B7280'],
  'financial-services': ['#2e873d', '#059669', '#0D9488', '#F59E0B', '#6366F1', '#9CA3AF'],
  'asset-management':   ['#059669', '#10B981', '#0D9488', '#F59E0B', '#6366F1', '#9CA3AF'],
  'property':           ['#1D4ED8', '#3B82F6', '#0EA5E9', '#F59E0B', '#7C3AED', '#9CA3AF'],
  'rates':              ['#1D4ED8', '#3B82F6', '#0EA5E9', '#F59E0B', '#7C3AED', '#9CA3AF'],
  'permits':            ['#1D4ED8', '#3B82F6', '#0EA5E9', '#F59E0B', '#7C3AED', '#9CA3AF'],
  'infringements':      ['#1D4ED8', '#3B82F6', '#0EA5E9', '#F59E0B', '#7C3AED', '#9CA3AF'],
  'abs-census':         ['#7C7CFF', '#38BDF8', '#34D399', '#FBBF24', '#F472B6', '#A8A2FF'],
  'economic-activity':  ['#F59E0B', '#FBBF24', '#0EA5E9', '#10B981', '#7C3AED', '#9CA3AF'],
};

function paletteForDatasets(datasetIds: string[]): string[] | undefined {
  for (const id of datasetIds || []) {
    if (DATASET_PALETTES[id]) return DATASET_PALETTES[id];
  }
  return undefined;
}

function defaultGridH(type: string): number {
  if (type === 'kpi' || type === 'number') return 2;
  if (type === 'table') return 5;
  return 4;
}

/** Strip verbs & suffixes from a user query to produce a clean widget title */
function deriveFrontendTitle(query: string): string {
  let t = query.trim();
  t = t.replace(/^(add|show|create|display|give\s+me\s+a?|get|what(?:'s|\s+is)?\s+(?:the\s+)?)\s*/i, '');
  t = t.replace(/\s+(as\s+a\s+widget|as\s+a\s+kpi|as\s+a\s+chart|as\s+a\s+table|as\s+a\s+card|as\s+a\s+number\s+card|as\s+a\s+(?:bar|line|pie|donut|area)\s+chart)\s*$/i, '');
  t = t.replace(/\b\w/g, (c) => c.toUpperCase());
  return t.slice(0, 60) || 'Widget';
}

/**
 * Extract the display value for a KPI / number card from fresh chart data
 * (first numeric-looking field of the first row — same detection as the
 * widget-add path). KPI cards render widget.kpiValue, not chartData, so
 * filter re-queries must refresh it explicitly or the card shows stale
 * numbers while charts update. Returns 0 when the filtered result is empty
 * so the card honestly reflects "nothing matches this filter".
 */
function extractKpiValue(chartData: { rows: Record<string, any>[]; fields: { name: string }[] }): string | number {
  if (chartData.rows.length > 0) {
    const row = chartData.rows[0];
    const numField = chartData.fields.find((f) => {
      const val = row[f.name];
      if (typeof val === 'number') return true;
      if (val === null || val === undefined || val === '') return false;
      return !isNaN(Number(String(val).replace(/[$,]/g, '')));
    });
    if (numField) return row[numField.name];
  }
  return 0;
}

/** Merge a filter re-query result onto a widget, refreshing the KPI value for value cards */
function applyFilteredData(widget: DashboardWidget, chartData: any): DashboardWidget {
  const updated = { ...widget, chartData, filterNotApplied: false };
  if (widget.type === 'kpi' || widget.type === 'number') {
    updated.kpiValue = extractKpiValue(chartData);
  }
  return updated;
}

/** Monotonic generation counter for filter re-queries. Each filter change
 *  bumps it; responses from an older generation are DISCARDED so a slow
 *  filtered query can never overwrite the data of a newer filter state
 *  (e.g. a late "Community Services" response landing after the user
 *  already cleared the filter back to "All"). */
const filterRequeryGen = { current: 0 };

/** Re-query every widget with the given filter set, marking widgets whose SQL
 *  can't take the filter (applied:false / error) so the UI shows they hold
 *  unfiltered data instead of silently pretending the filter worked. */
function requeryWidgetsWithFilters(
  widgets: DashboardWidget[],
  filterParams: { field: string; value: string }[],
  datasetIds: string[],
  setWidgets: React.Dispatch<React.SetStateAction<DashboardWidget[]>>,
) {
  const hasActiveFilters = filterParams.length > 0;
  const myGen = ++filterRequeryGen.current;
  for (const w of widgets.filter((x) => x.originalSql)) {
    const projectedFields = (w.chartData?.fields || []).map((f) => f.name);
    dashboardBuilderService.filterQuery(w.originalSql!, filterParams, datasetIds, projectedFields)
      .then((result) => {
        if (myGen !== filterRequeryGen.current) return; // superseded by a newer filter change
        if (result?.applied === false) {
          setWidgets((prev) => prev.map((pw) => pw.id === w.id
            ? { ...pw, filterNotApplied: hasActiveFilters }
            : pw));
        } else if (result?.chartData?.rows && result?.chartData?.fields) {
          setWidgets((prev) => prev.map((pw) => pw.id === w.id ? applyFilteredData(pw, result.chartData) : pw));
        }
      })
      .catch((err) => {
        if (myGen !== filterRequeryGen.current) return;
        console.error(`Filter re-query failed for ${w.title}:`, err);
        setWidgets((prev) => prev.map((pw) => pw.id === w.id
          ? { ...pw, filterNotApplied: hasActiveFilters }
          : pw));
      });
  }
}

export default function DashboardBuilder() {
  // View state
  const [view, setView] = useState<ViewState>('picker');

  // Dashboard state
  const [dashboardId, setDashboardId] = useState<string | null>(null);
  const [dashboardName, setDashboardName] = useState('Untitled Dashboard');
  const [datasetIds, setDatasetIds] = useState<string[]>([]);
  const [datasetNames, setDatasetNames] = useState<string[]>([]);
  const [widgets, setWidgets] = useState<DashboardWidget[]>([]);
  const [chatMessages, setChatMessages] = useState<BuilderChatMessage[]>([]);
  const [filters, setFilters] = useState<DashboardFilter[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionGroups, setSuggestionGroups] = useState<SuggestionGroup[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateOffered, setTemplateOffered] = useState<string | null>(null);

  // ── Load a curated template (finance / people) ──
  const handleLoadTemplate = useCallback(async () => {
    const meta = templateOffered ? CURATED_TEMPLATES[templateOffered] : undefined;
    if (!meta) return;
    setTemplateLoading(true);
    setTemplateOffered(null);
    try {
      const tpl = await dashboardBuilderService.getTemplate(meta.key);

      // Build widgets with grid positions
      const newWidgets: DashboardWidget[] = [];
      // Process by tab so each tab's layout starts from row 0
      const tabGroups = new Map<string, typeof tpl.widgets>();
      for (const w of tpl.widgets) {
        const tab = w.tabId || '__none__';
        if (!tabGroups.has(tab)) tabGroups.set(tab, []);
        tabGroups.get(tab)!.push(w);
      }

      for (const [, tabWidgets] of tabGroups) {
        const tabContext: DashboardWidget[] = [];
        for (const w of tabWidgets) {
          const widgetType = w.type || 'chart';
          const widgetSize = w.size || 'medium';
          const gridW = sizeToGridW(widgetSize, widgetType);
          const gridH = defaultGridH(widgetType);
          const { gridX, gridY } = computeGridPosition(tabContext, gridW, gridH);

          const widget: DashboardWidget = {
            id: w.id || `widget-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            type: widgetType,
            title: w.title,
            position: newWidgets.length,
            size: widgetSize,
            chartType: w.chartType,
            chartData: w.chartData,
            kpiValue: w.kpiValue,
            kpiLabel: w.kpiLabel,
            kpiIcon: w.kpiIcon,
            kpiTrend: w.kpiTrend,
            colorScheme: w.colorScheme,
            originalSql: w.originalSql,
            gridX,
            gridY,
            gridW,
            gridH,
            tabId: w.tabId,
          };
          newWidgets.push(widget);
          tabContext.push(widget);
        }
      }

      setWidgets(newWidgets);
      setDashboardName(meta.dashName);

      // Load template filters
      if (tpl.filters && tpl.filters.length > 0) {
        setFilters(tpl.filters.map((f: any) => ({
          id: f.id,
          field: f.field,
          label: f.label,
          values: f.values || [],
          selectedValue: '',
          parentField: f.parentField || undefined,
        })));
      }

      // Add system message to chat
      const sysMsg: BuilderChatMessage = {
        id: `msg-${Date.now()}-tpl`,
        role: 'assistant',
        content: meta.loadedMessage(newWidgets.length),
        timestamp: new Date().toISOString(),
        suggestions: meta.suggestions,
      };
      setChatMessages((prev) => [...prev, sysMsg]);
      setSuggestions(meta.suggestions);
    } catch (err) {
      console.error('Failed to load template:', err);
      const errMsg: BuilderChatMessage = {
        id: `msg-${Date.now()}-terr`,
        role: 'assistant',
        content: `Failed to load the ${meta.label} template. You can still build widgets manually using the chat.`,
        timestamp: new Date().toISOString(),
      };
      setChatMessages((prev) => [...prev, errMsg]);
    } finally {
      setTemplateLoading(false);
    }
  }, [templateOffered]);

  // ── Start building from dataset picker ──
  const handleStart = useCallback(async (ids: string[], names: string[]) => {
    setDatasetIds(ids);
    setDatasetNames(names);
    const groups = getGroupedSuggestions(names);
    setSuggestionGroups(groups);
    setSuggestions(flattenSuggestions(groups));

    // Offer the curated template matching the selected dataset
    const lower = names.map((n) => n.toLowerCase());
    if (lower.some((n) => n.includes('finance'))) {
      setTemplateOffered('finance');
    } else if (lower.some((n) => n.includes('people'))) {
      setTemplateOffered('people');
    } else {
      setTemplateOffered(null);
    }

    // Create a new dashboard in the backend
    try {
      const dashboard = await dashboardBuilderService.createDashboard('Untitled Dashboard', ids);
      setDashboardId(dashboard.id);
    } catch (err) {
      console.error('Failed to create dashboard:', err);
      // Continue without persistence
    }

    setView('builder');
  }, []);

  // ── Load a saved dashboard ──
  const handleLoadSaved = useCallback(async (id: string) => {
    try {
      const dashboard: SavedDashboard = await dashboardBuilderService.getDashboard(id);
      setDashboardId(dashboard.id);
      setDashboardName(dashboard.name);
      setDatasetIds(dashboard.datasetIds);
      setDatasetNames(dashboard.datasetIds); // Use IDs as names if no mapping
      setWidgets(dashboard.widgets || []);
      setChatMessages(dashboard.chatHistory || []);
      const groups = getGroupedSuggestions(dashboard.datasetIds);
      setSuggestionGroups(groups);
      setSuggestions(flattenSuggestions(groups));
      setView('builder');
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    }
  }, []);

  // ── Save dashboard ──
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      // If the initial create failed (or never ran), create on the fly so save always persists
      let id = dashboardId;
      if (!id) {
        const created = await dashboardBuilderService.createDashboard(dashboardName, datasetIds);
        id = created.id;
        setDashboardId(id);
      }
      await dashboardBuilderService.updateDashboard(id, {
        name: dashboardName,
        widgets,
        chatHistory: chatMessages,
      });
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 2500);
    } catch (err) {
      console.error('Failed to save dashboard:', err);
      const msg = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        || (err as { message?: string })?.message
        || 'Unknown error';
      alert(`Failed to save dashboard: ${msg}`);
    } finally {
      setSaving(false);
    }
  }, [dashboardId, dashboardName, datasetIds, widgets, chatMessages]);

  // ── Chat: send a message ──
  const handleSend = useCallback(async (text: string) => {
    // Add user message
    const userMsg: BuilderChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    setChatMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const response = await dashboardBuilderService.query({
        query: text,
        datasetIds,
        existingWidgets: widgets,
      });

      // ── Handle action: add / modify / remove ──
      let widgetAction: BuilderChatMessage['widgetAction'] | undefined;

      if (response.action === 'add_widget' && response.widgets && response.widgets.length > 0) {
        // ── ADD NEW WIDGETS ──
        // Sort: number/kpi first so they pack on top row together
        const typeOrder: Record<string, number> = { number: 0, kpi: 1, chart: 2, table: 3 };
        const sortedResponseWidgets = [...response.widgets].sort(
          (a, b) => (typeOrder[a.type || 'chart'] ?? 2) - (typeOrder[b.type || 'chart'] ?? 2)
        );

        const newWidgets: DashboardWidget[] = [];
        // Track cumulative layout including existing + already-placed new widgets
        const layoutContext = [...widgets];

        for (const w of sortedResponseWidgets) {
          // Prefer per-widget chartData (multi-widget), fall back to global
          const widgetChartData = w.chartData || response.chartData || undefined;

          const widgetType = w.type || 'chart';
          // Number/KPI default to small, tables to large, charts to medium
          const widgetSize = w.size || (widgetType === 'number' || widgetType === 'kpi' ? 'small' : widgetType === 'table' ? 'large' : 'medium');
          const gridW = sizeToGridW(widgetSize, widgetType);
          const gridH = defaultGridH(widgetType);

          // Compute grid position based on existing + already-placed widgets
          const { gridX, gridY } = computeGridPosition(layoutContext, gridW, gridH);

          const widget: DashboardWidget = {
            id: `widget-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            type: widgetType,
            title: w.title || deriveFrontendTitle(text),
            position: widgets.length + newWidgets.length,
            size: widgetSize,
            chartType: w.chartType,
            chartData: widgetChartData,
            kpiValue: w.kpiValue,
            kpiLabel: w.kpiLabel,
            kpiIcon: w.kpiIcon,
            kpiTrend: w.kpiTrend,
            query: text,
            // Dataset palette applies to charts only — KPI/number values render
            // near-black like the governed dashboards unless the AI explicitly
            // picks a colour scheme for emphasis.
            colorScheme: w.colorScheme ||
              (widgetType === 'chart' ? paletteForDatasets(datasetIds) : undefined),
            originalSql: w.originalSql,
            gridX,
            gridY,
            gridW,
            gridH,
            tabId: w.tabId,
          };

          // If it's a KPI or number and we got chart data with a single row, extract the value
          if ((widget.type === 'kpi' || widget.type === 'number') && widgetChartData && widgetChartData.rows && widgetChartData.rows.length > 0 && widgetChartData.fields) {
            const row = widgetChartData.rows[0];
            const fields = widgetChartData.fields;
            const numField = fields.find((f: { name: string }) => {
              const val = row[f.name];
              return typeof val === 'number' || !isNaN(Number(String(val).replace(/[$,]/g, '')));
            });
            if (numField && !widget.kpiValue) {
              widget.kpiValue = row[numField.name];
            }
          }

          newWidgets.push(widget);
          layoutContext.push(widget); // Track for next widget's position calculation
        }
        if (newWidgets.length > 0) {
          setWidgets((prev) => [...prev, ...newWidgets]);
          widgetAction = { action: 'add_widget', widget: newWidgets[0] };
        }

      } else if (response.action === 'modify_widget' && response.widgets && response.widgets.length > 0) {
        // ── MODIFY EXISTING WIDGETS ──
        for (const w of response.widgets) {
          // Match by ID from AI, or fall back to fuzzy title match
          const matchId = w.id;
          setWidgets((prev) => prev.map((existing) => {
            const isMatch = matchId
              ? existing.id === matchId
              : existing.title.toLowerCase() === (w.title || '').toLowerCase();
            if (!isMatch) return existing;
            // Merge only the properties the AI returned
            return {
              ...existing,
              ...(w.title ? { title: w.title } : {}),
              ...(w.size ? { size: w.size } : {}),
              ...(w.chartType ? { chartType: w.chartType } : {}),
              ...(w.colorScheme ? { colorScheme: w.colorScheme } : {}),
              ...(w.kpiLabel ? { kpiLabel: w.kpiLabel } : {}),
              ...(w.kpiIcon ? { kpiIcon: w.kpiIcon } : {}),
              ...(w.kpiTrend ? { kpiTrend: w.kpiTrend } : {}),
              ...(w.type ? { type: w.type } : {}),
              // If the AI returned new chart data (re-queried), apply it
              ...(response.chartData ? { chartData: response.chartData } : {}),
            };
          }));
          widgetAction = { action: 'modify_widget', widgetId: matchId || w.title };
        }

      } else if (response.action === 'remove_widget' && response.widgets && response.widgets.length > 0) {
        // ── REMOVE WIDGETS ──
        for (const w of response.widgets) {
          const matchId = w.id;
          setWidgets((prev) => prev.filter((existing) => {
            if (matchId) return existing.id !== matchId;
            return existing.title.toLowerCase() !== (w.title || '').toLowerCase();
          }));
          widgetAction = { action: 'remove_widget', widgetId: matchId || w.title };
        }
      }

      // ── Handle add_filter ──
      // Resolved server-side against the curated per-dataset filter catalogue
      // first; model-authored values SQL is only a fallback. On failure the
      // failure note REPLACES the model's message (which claims success) so the
      // chat never contradicts itself.
      let filterNote: string | null = null;
      if (response.action === 'add_filter' && response.filter) {
        const { field, label, sql } = response.filter;
        try {
          const resolved = await dashboardBuilderService.resolveFilter(field, label, sql, datasetIds);
          if (!resolved.values || resolved.values.length === 0) {
            filterNote = `I couldn't add a "${label || field}" filter — no values came back for that field in the connected data.`;
          } else {
            const newFilter: DashboardFilter = {
              id: `filter-${Date.now()}`,
              field: resolved.field,
              label: resolved.label,
              values: resolved.values,
              selectedValue: '',
              parentField: resolved.parentField || undefined,
            };
            setFilters((prev) => [...prev, newFilter]);
          }
        } catch (err2: any) {
          console.error('Failed to resolve filter:', err2);
          const avail = err2?.response?.data?.available as { field: string; label: string }[] | undefined;
          filterNote = `I couldn't add the "${label || field}" filter — its values couldn't be read from the connected dataset.${
            avail && avail.length > 0
              ? ` Filters available for this data: ${avail.map((a) => a.label).join(', ')}.`
              : ' Try naming a field that appears in one of your current widgets.'
          }`;
        }
      }

      // Add assistant message. A failed filter REPLACES the model's message —
      // the model text says "I've added the filter", which would contradict the
      // failure note if we appended.
      const baseMessage = response.message || "Here's what I've done.";
      const assistantMsg: BuilderChatMessage = {
        id: `msg-${Date.now()}-a`,
        role: 'assistant',
        content: filterNote ?? baseMessage,
        timestamp: new Date().toISOString(),
        suggestions: response.suggestions,
        widgetAction,
      };
      setChatMessages((prev) => [...prev, assistantMsg]);
      setSuggestions(response.suggestions || []);

    } catch (err) {
      console.error('Dashboard builder query failed:', err);
      const errMsg: BuilderChatMessage = {
        id: `msg-${Date.now()}-e`,
        role: 'assistant',
        content: "Sorry, I couldn't process that request. Could you try rephrasing?",
        timestamp: new Date().toISOString(),
        suggestions: ['Try a simpler query', 'Ask what data is available'],
      };
      setChatMessages((prev) => [...prev, errMsg]);
    } finally {
      setLoading(false);
    }
  }, [datasetIds, widgets]);

  // ── Close / New Dashboard ──
  const resetDashboard = useCallback(() => {
    setDashboardId(null);
    setDashboardName('Untitled Dashboard');
    setDatasetIds([]);
    setDatasetNames([]);
    setWidgets([]);
    setFilters([]);
    setChatMessages([]);
    setSuggestions([]);
    setSuggestionGroups([]);
    setTemplateOffered(null);
    setTemplateLoading(false);
    setView('picker');
  }, []);

  const handleNewDashboard = useCallback(() => {
    resetDashboard();
  }, [resetDashboard]);

  const handleCloseDashboard = useCallback(() => {
    resetDashboard();
  }, [resetDashboard]);

  const handleClearChat = useCallback(() => {
    setChatMessages([]);
    const groups = getGroupedSuggestions(datasetNames);
    setSuggestionGroups(groups);
    setSuggestions(flattenSuggestions(groups));
  }, [datasetNames]);

  // ── Export / Share PDF ──
  const buildPdf = useCallback(async (output: 'download' | 'blob') => {
    const { exportDashboardToPdf } = await import('../utils/exportDashboardToPdf');
    // Collect chart widget DOM elements
    const chartElements = new Map<string, HTMLElement>();
    for (const w of widgets) {
      if (w.type === 'chart') {
        const el = document.querySelector(`[data-widget-id="${w.id}"]`) as HTMLElement | null;
        if (el) chartElements.set(w.id, el);
      }
    }
    return exportDashboardToPdf({
      dashboardName,
      widgets,
      datasetNames,
      chartElements,
      output,
    });
  }, [dashboardName, widgets, datasetNames]);

  const handleExportPdf = useCallback(async () => {
    setExporting(true);
    try {
      await buildPdf('download');
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [buildPdf]);

  // Share the report through the signed-in user's own M365: Email creates an
  // Outlook draft with the PDF attached (it syncs to desktop Outlook's Drafts
  // — this is an intranet, Outlook is installed everywhere, so we nudge the
  // desktop client open instead of forcing an Outlook Web sign-in); Teams
  // uploads to OneDrive and opens a new chat with the report link prefilled.
  // Ordering matters for the popup blocker: the Graph token (and its one-time
  // consent popup) is acquired FIRST, inside the click's user-gesture window,
  // and for Teams a placeholder tab for the final link is claimed BEFORE the
  // slow PDF generation. Opening it after seconds of html2canvas work gets
  // blocked.
  const handleShare = useCallback(async (channel: 'email' | 'teams') => {
    setExporting(true);
    let placeholder: Window | null = null;
    try {
      const share = await import('../services/shareService');
      const token = await share.acquireShareToken(channel);

      if (channel === 'teams') {
        placeholder = window.open('', '_blank');
        if (placeholder) {
          try {
            placeholder.document.title = 'Preparing report…';
            const note = placeholder.document.createElement('p');
            note.textContent = 'Preparing the dashboard report…';
            note.style.cssText = 'font-family:Segoe UI,sans-serif;color:#484848;padding:40px';
            placeholder.document.body.appendChild(note);
          } catch { /* cross-origin quirks — placeholder still usable */ }
        }
      }

      const pdf = await buildPdf('blob');
      if (!pdf) throw new Error('PDF generation returned nothing');

      if (channel === 'email') {
        await share.shareReportViaEmail(pdf.blob, pdf.fileName, dashboardName, token);
        // Bring desktop Outlook to the foreground. The protocol launch can't
        // deep-link the draft itself, but the draft is already syncing into
        // the Drafts folder. A hidden iframe avoids navigating this page.
        try {
          const nudge = document.createElement('iframe');
          nudge.style.display = 'none';
          nudge.src = 'ms-outlook://';
          document.body.appendChild(nudge);
          setTimeout(() => nudge.remove(), 3000);
        } catch { /* protocol not registered — the message below still guides the user */ }
        alert('The report PDF is attached to a new draft in your Outlook Drafts folder. Open Outlook, add recipients and send. (If Outlook asks to open, choose Open.)');
      } else {
        const result = await share.shareReportViaTeams(pdf.blob, pdf.fileName, dashboardName, token, placeholder);
        if (!result.opened) {
          alert('The report was uploaded, but your browser blocked the Teams window — allow popups for this site and share again.');
        }
      }
    } catch (err: any) {
      if (placeholder && !placeholder.closed) { try { placeholder.close(); } catch { /* ignore */ } }
      console.error(`Share via ${channel} failed:`, err);
      alert(err?.message || `Sharing via ${channel === 'email' ? 'email' : 'Teams'} failed. If this is the first use, your administrator may need to approve the Microsoft 365 permissions.`);
    } finally {
      setExporting(false);
    }
  }, [buildPdf, dashboardName]);

  // ── Widget actions ──
  const handleRemoveWidget = useCallback((id: string) => {
    setWidgets((prev) => prev.filter((w) => w.id !== id));
  }, []);

  const handleResizeWidget = useCallback((id: string, size: 'small' | 'medium' | 'large') => {
    setWidgets((prev) => prev.map((w) => {
      if (w.id !== id) return w;
      return { ...w, size, gridW: sizeToGridW(size, w.type) };
    }));
  }, []);

  const handleChangeChartType = useCallback((id: string, chartType: ChartPanelChartType) => {
    setWidgets((prev) => prev.map((w) => w.id === id ? { ...w, chartType } : w));
  }, []);

  const handleUpdateLegendLabels = useCallback((id: string, legendLabels: Record<string, string>) => {
    setWidgets((prev) => prev.map((w) => w.id === id ? { ...w, legendLabels } : w));
  }, []);

  // ── Filter handlers ──
  // Cascading: filters with a parentField (Department cascades from
  // Directorate) get their VALUES re-scoped when the parent selection changes,
  // and their selection cleared so a stale department never combines with the
  // new directorate.
  const handleFilterChange = useCallback((filterId: string, value: string) => {
    setFilters((prev) => {
      const changed = prev.find((f) => f.id === filterId);
      if (!changed) return prev;

      let next = prev.map((f) => (f.id === filterId ? { ...f, selectedValue: value } : f));
      const dependents = next.filter(
        (f) => f.parentField && f.parentField.toLowerCase() === changed.field.toLowerCase()
      );
      if (dependents.length > 0) {
        next = next.map((f) =>
          dependents.some((d) => d.id === f.id) ? { ...f, selectedValue: '' } : f
        );
      }

      // Re-query widgets with the final selections
      const filterParams = next
        .filter((f) => f.selectedValue)
        .map((f) => ({ field: f.field, value: f.selectedValue }));
      setWidgets((currentWidgets) => {
        requeryWidgetsWithFilters(currentWidgets, filterParams, datasetIds, setWidgets);
        return currentWidgets;
      });

      // Re-scope dependent dropdowns to the new parent value (async; a cleared
      // parent restores the full list)
      for (const dep of dependents) {
        dashboardBuilderService
          .resolveFilter(dep.field, dep.label, undefined, datasetIds, { field: changed.field, value })
          .then((resolved) => {
            if (resolved?.values) {
              setFilters((cur) => cur.map((f) => (f.id === dep.id ? { ...f, values: resolved.values } : f)));
            }
          })
          .catch((err) => console.error(`Failed to re-scope ${dep.label} filter:`, err));
      }

      return next;
    });
  }, [datasetIds]);

  const handleRemoveFilter = useCallback((filterId: string) => {
    setFilters((prev) => {
      const removed = prev.find((f) => f.id === filterId);
      let next = prev.filter((f) => f.id !== filterId);

      // Removing a parent filter restores its dependents to the full value list
      const dependents = removed
        ? next.filter((f) => f.parentField && f.parentField.toLowerCase() === removed.field.toLowerCase())
        : [];
      for (const dep of dependents) {
        dashboardBuilderService
          .resolveFilter(dep.field, dep.label, undefined, datasetIds)
          .then((resolved) => {
            if (resolved?.values) {
              setFilters((cur) => cur.map((f) => (f.id === dep.id ? { ...f, values: resolved.values } : f)));
            }
          })
          .catch((err) => console.error(`Failed to re-scope ${dep.label} filter:`, err));
      }

      const filterParams = next
        .filter((f) => f.selectedValue)
        .map((f) => ({ field: f.field, value: f.selectedValue }));
      setWidgets((currentWidgets) => {
        requeryWidgetsWithFilters(currentWidgets, filterParams, datasetIds, setWidgets);
        return currentWidgets;
      });
      return next;
    });
  }, [datasetIds]);

  const handleLayoutChange = useCallback((layout: Layout) => {
    setWidgets((prev) => prev.map((w) => {
      const item = layout.find((l) => l.i === w.id);
      if (!item) return w;
      return { ...w, gridX: item.x, gridY: item.y, gridW: item.w, gridH: item.h };
    }));
  }, []);

  const handleChangeWidgetType = useCallback((id: string, newType: WidgetType) => {
    setWidgets((prev) => prev.map((w) => {
      if (w.id !== id) return w;
      if (newType === 'kpi' || newType === 'number') {
        // Extract first numeric value from chartData for KPI/number
        let kpiValue = w.kpiValue;
        if (!kpiValue && w.chartData && w.chartData.rows.length > 0) {
          const row = w.chartData.rows[0];
          const numField = (w.chartData.fields || []).find((f) => {
            const val = row[f.name];
            return typeof val === 'number' || !isNaN(Number(String(val).replace(/[$,]/g, '')));
          });
          if (numField) kpiValue = row[numField.name];
        }
        if (newType === 'number') {
          return { ...w, type: 'number', size: 'small', gridW: 3, gridH: 2, kpiValue };
        }
        return { ...w, type: 'kpi', size: 'small', gridW: 3, gridH: 2, kpiLabel: w.kpiLabel || w.title, kpiValue };
      }
      if (newType === 'table') {
        return { ...w, type: 'table', size: 'large', gridW: 12, gridH: 5 };
      }
      // chart
      const newSize = w.size === 'small' ? 'medium' : w.size;
      return { ...w, type: 'chart', chartType: w.chartType || 'bar', size: newSize, gridW: newSize === 'large' ? 12 : 6, gridH: 4 };
    }));
  }, []);

  // ── Render ──
  if (view === 'saved') {
    return (
      <SavedDashboardsList
        onOpen={handleLoadSaved}
        onBack={() => setView('picker')}
      />
    );
  }

  if (view === 'picker') {
    return (
      <DatasetPicker
        onStart={handleStart}
        onLoadSaved={() => setView('saved')}
      />
    );
  }

  // Builder view
  return (
    <div style={{
      display: 'flex',
      height: 'calc(100vh - 52px)',
      overflow: 'hidden',
    }}>
      {/* Template offer banner */}
      {templateOffered && widgets.length === 0 && (
        <div style={{
          position: 'absolute',
          top: 64,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 100,
          background: '#FFFFFF',
          border: '1px solid #2e873d',
          borderRadius: 8,
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}>
          <span style={{ fontSize: 13, color: '#232323', fontWeight: 500 }}>
            Use the {templateOffered ? CURATED_TEMPLATES[templateOffered]?.label : ''} template?
          </span>
          <button
            onClick={handleLoadTemplate}
            disabled={templateLoading}
            style={{
              padding: '6px 16px',
              borderRadius: 4,
              background: '#2e873d',
              color: '#FFFFFF',
              border: 'none',
              fontSize: 12,
              fontWeight: 600,
              cursor: templateLoading ? 'default' : 'pointer',
              opacity: templateLoading ? 0.6 : 1,
            }}
          >
            {templateLoading ? 'Loading...' : 'Load Template'}
          </button>
          <button
            onClick={() => setTemplateOffered(null)}
            style={{
              padding: '6px 12px',
              borderRadius: 4,
              background: 'transparent',
              color: '#747474',
              border: '1px solid #E1E1E1',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            No thanks
          </button>
        </div>
      )}
      <DashboardCanvas
        dashboardName={dashboardName}
        onNameChange={setDashboardName}
        widgets={widgets}
        datasetNames={datasetNames}
        accentColor={paletteForDatasets(datasetIds)?.[0] ?? '#2e873d'}
        filters={filters}
        onFilterChange={handleFilterChange}
        onRemoveFilter={handleRemoveFilter}
        onRemoveWidget={handleRemoveWidget}
        onResizeWidget={handleResizeWidget}
        onChangeChartType={handleChangeChartType}
        onChangeWidgetType={handleChangeWidgetType}
        onUpdateLegendLabels={handleUpdateLegendLabels}
        onLayoutChange={handleLayoutChange}
        onSave={handleSave}
        saving={saving}
        justSaved={justSaved}
        onExportPdf={handleExportPdf}
        onShare={handleShare}
        exporting={exporting}
        onClose={handleCloseDashboard}
        onNewDashboard={handleNewDashboard}
        suggestionChips={widgets.length === 0 ? suggestions : undefined}
        suggestionGroups={widgets.length === 0 ? suggestionGroups : undefined}
        onSuggestionClick={handleSend}
      />
      <BuilderChat
        messages={chatMessages}
        onSend={handleSend}
        loading={loading}
        datasetNames={datasetNames}
        suggestions={suggestions}
        onSuggestionClick={handleSend}
        onClearChat={handleClearChat}
        isCollapsed={chatCollapsed}
        onToggleCollapse={() => setChatCollapsed(v => !v)}
      />
    </div>
  );
}
